import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, fmtDate, fmtDateTime, fmtINR, downloadAuthed } from "@/lib/api";
import { PageHeader, PrimaryButton } from "@/components/DataViews";
import StatusBadge from "@/components/StatusBadge";
import DocumentTrail from "@/components/DocumentTrail";
import { AdminVoidRestore, AdminEditBanner } from "@/components/AdminActions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Download, FileText, EyeOff, PackageCheck, Wallet, PackagePlus, Banknote } from "lucide-react";

function DeliveryBar({ pct }) {
  const clamp = Math.max(0, Math.min(100, Number(pct || 0)));
  const color = clamp >= 100 ? "bg-emerald-500" : clamp > 50 ? "bg-kabir" : clamp > 0 ? "bg-blue-500" : "bg-zinc-300 dark:bg-zinc-700";
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${clamp}%` }} />
      </div>
      <span className="text-[11px] font-mono tabular-nums w-10 text-right text-muted-foreground">{clamp.toFixed(0)}%</span>
    </div>
  );
}

export default function PODetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { pricingHidden, moduleHidden, fieldHidden, hasRole } = useAuth();
  const [po, setPO] = useState(null);
  const [audit, setAudit] = useState([]);
  const [grns, setGrns] = useState([]);
  const [adv, setAdv] = useState(null);
  const [grnOpen, setGrnOpen] = useState(false);
  const [grnForm, setGrnForm] = useState(null);
  const [advOpen, setAdvOpen] = useState(false);
  const [advPayForm, setAdvPayForm] = useState({ utr: "", mode: "NEFT", bank: "", date: "" });

  const load = useCallback(async () => {
    const r = await api.get(`/pos/${id}`);
    setPO(r.data);
    const [gs, av, au] = await Promise.all([
      api.get(`/pos/${id}/grns`).then((x) => x.data).catch(() => []),
      api.get(`/pos/${id}/advance-status`).then((x) => x.data).catch(() => null),
      api.get(`/audit-logs?entity_id=${id}`).then((x) => x.data).catch(() => []),
    ]);
    setGrns(gs); setAdv(av); setAudit(au);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!po) return <div>Loading…</div>;

  const downloadPDF = async () => {
    try {
      await downloadAuthed(`/pos/${id}/pdf`, `${(po.po_number || "PO").replace(/\//g, "_")}.pdf`);
    } catch { toast.error("PDF download failed"); }
  };
  const previewPDF = () => {
    fetch(`${API}/pos/${id}/pdf`, { credentials: "include" })
      .then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank"));
  };

  const isFullyDelivered = (po.delivered_pct || 0) >= 100;
  const canBookGRN = hasRole("Administrator", "Store Keeper") && po.status === "Active" && !po.is_voided;

  const openBookGRN = () => {
    setGrnForm({
      po_id: id, challan_no: "", vehicle_no: "", driver_name: "", batch_no: "", heat_no: "",
      manufacturer: "", remarks: "",
      items: (po.items || []).map((it, i) => ({
        po_item_index: i, material_name: it.material_name, unit: it.unit,
        ordered_qty: it.qty, previously_received: it.delivered_qty || 0,
        balance_qty: it.pending_qty ?? it.qty,
        received_qty: it.pending_qty ?? it.qty, accepted_qty: it.pending_qty ?? it.qty,
        rejected_qty: 0, rate: it.rate || 0, remarks: "",
      })),
    });
    setGrnOpen(true);
  };
  const saveGRN = async () => {
    try {
      const { data } = await api.post("/grns", grnForm);
      toast.success(`GRN ${data.grn_no} recorded`);
      setGrnOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const canIssueAdvancePV = hasRole("Administrator", "Accounts Executive") &&
    (po.advance_amount || 0) > 0 && !po.advance_paid && !po.is_voided;
  const createAdvancePV = async () => {
    try {
      const { data } = await api.post("/pvs", {
        po_id: id, voucher_kind: "Advance Payment", payment_mode: "NEFT",
      });
      toast.success(`Advance PV ${data.pv_no} created`);
      nav("/pvs");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const canMarkAdvancePaidOutside = hasRole("Administrator", "Purchase Manager") &&
    (po.advance_amount || 0) > 0 && !po.advance_paid && !po.is_voided;
  const markAdvancePaidOutside = async () => {
    if (!advPayForm.utr) { toast.error("UTR / Cheque no required"); return; }
    try {
      await api.put(`/admin/entity/po/${id}`, {
        _admin_reason: `Advance recorded as paid outside system (UTR ${advPayForm.utr})`,
        advance_paid: true,
        advance_utr: advPayForm.utr,
        advance_payment_mode: advPayForm.mode,
        advance_bank: advPayForm.bank,
        advance_date: advPayForm.date || new Date().toISOString().slice(0, 10),
        advance_paid_at: new Date().toISOString(),
      });
      toast.success("Advance recorded as paid");
      setAdvOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Only Administrator can record outside payment"); }
  };

  return (
    <div data-testid="po-detail">
      <PageHeader
        title={<span className="font-mono">{po.po_number || po.draft_po_id}</span>}
        subtitle={`${po.vendor_name} • ${po.project_name || "-"}${fieldHidden("po", "grand_total") ? "" : " • " + fmtINR(po.grand_total)}`}
        actions={
          <>
            {!moduleHidden("po_pdf") && (
              <>
                <Button variant="outline" onClick={previewPDF} data-testid="preview-pdf-btn"><FileText size={14} className="mr-2" />Preview</Button>
                <PrimaryButton onClick={downloadPDF} data-testid="download-pdf-btn"><Download size={14} className="mr-2" />Download PDF</PrimaryButton>
              </>
            )}
            {canBookGRN && (
              <PrimaryButton onClick={openBookGRN} data-testid="book-grn-inline-btn">
                <PackagePlus size={14} className="mr-2" />Book GRN
              </PrimaryButton>
            )}
            <AdminVoidRestore entity="po" id={id} isVoided={po.is_voided} onDone={load} />
          </>
        }
      />

      <AdminEditBanner doc={po} />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Vendor</div>
              <div className="font-display text-xl font-semibold">{po.vendor_name}</div>
              <div className="text-xs text-muted-foreground">{po.vendor_address}</div>
              <div className="text-xs text-muted-foreground font-mono">GSTIN: {po.vendor_gstin || "-"}</div>
            </div>
            <StatusBadge status={po.is_voided ? "Voided" : po.status} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm border-t border-border pt-4">
            <div><div className="text-[10px] uppercase text-muted-foreground">Delivery Date</div>{fmtDate(po.delivery_date)}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Payment</div>{po.payment_terms}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Created By</div>{po.created_by_name}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">GRNs Recorded</div>{po.grn_count ?? 0}</div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Line Items — Delivery Progress</div>
              <div className="flex items-center gap-3">
                <div className="w-40"><DeliveryBar pct={po.delivered_pct} /></div>
                {isFullyDelivered && <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-widest flex items-center gap-1"><PackageCheck size={12} />Fully Delivered</span>}
              </div>
            </div>
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Material</th>
                    <th className="text-left px-3 py-2">Unit</th>
                    <th className="text-right px-3 py-2">Ordered</th>
                    <th className="text-right px-3 py-2 text-emerald-700">Delivered</th>
                    <th className="text-right px-3 py-2 text-amber-700">Pending</th>
                    <th className="text-left px-3 py-2">Progress</th>
                    {!fieldHidden("po", "items.rate") && <th className="text-right px-3 py-2">Rate</th>}
                    {!fieldHidden("po", "items.total") && <th className="text-right px-3 py-2">Total</th>}
                  </tr>
                </thead>
                <tbody>
                  {po.items.map((it, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{it.material_name}</td>
                      <td className="px-3 py-2">{it.unit}</td>
                      <td className="px-3 py-2 text-right font-mono">{it.qty}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700 font-semibold">{it.delivered_qty ?? 0}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-700 font-semibold">{it.pending_qty ?? it.qty}</td>
                      <td className="px-3 py-2"><DeliveryBar pct={it.delivered_pct} /></td>
                      {!fieldHidden("po", "items.rate") && <td className="px-3 py-2 text-right font-mono">{fmtINR(it.rate)}</td>}
                      {!fieldHidden("po", "items.total") && <td className="px-3 py-2 text-right font-mono font-semibold">{fmtINR(it.total)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {!fieldHidden("po", "grand_total") && (
            <div className="mt-4 flex justify-end">
              <div className="w-full md:w-80 space-y-1 text-sm">
                {!fieldHidden("po", "subtotal") && <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{fmtINR(po.subtotal)}</span></div>}
                {!fieldHidden("po", "gst_total") && <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span className="font-mono">{fmtINR(po.gst_total)}</span></div>}
                {!fieldHidden("po", "freight") && <div className="flex justify-between"><span className="text-muted-foreground">Freight</span><span className="font-mono">{fmtINR(po.freight)}</span></div>}
                {!fieldHidden("po", "other_charges") && <div className="flex justify-between"><span className="text-muted-foreground">Other</span><span className="font-mono">{fmtINR(po.other_charges)}</span></div>}
                <div className="flex justify-between border-t border-border pt-2 mt-2 bg-kabir/10 -mx-2 px-2 py-1.5 rounded"><span className="font-semibold">Grand Total</span><span className="font-mono font-bold text-lg">{fmtINR(po.grand_total)}</span></div>
              </div>
            </div>
          )}
          {pricingHidden && (
            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground bg-secondary/50 px-3 py-2 rounded-md border border-border" data-testid="po-pricing-hidden">
              <EyeOff size={14} />
              <span>Pricing details are restricted for your role.</span>
            </div>
          )}

          {/* ---- Vendor Advance Card ---- */}
          {(po.advance_amount || 0) > 0 && !fieldHidden("po", "grand_total") && (
            <div className="mt-6 rounded-lg border border-kabir/40 bg-kabir/5 p-4" data-testid="po-advance-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Wallet size={16} className="text-kabir-dark" />
                  <span className="font-display text-sm font-semibold uppercase tracking-widest">Vendor Advance</span>
                </div>
                <div className="flex items-center gap-2">
                  {po.advance_paid
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 font-semibold uppercase tracking-widest">Paid</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-semibold uppercase tracking-widest">Pending</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><div className="text-[10px] uppercase text-muted-foreground">Amount</div><span className="font-mono font-semibold">{fmtINR(po.advance_amount)}</span></div>
                <div><div className="text-[10px] uppercase text-muted-foreground">% of PO</div>{po.advance_percent || 0}%</div>
                <div><div className="text-[10px] uppercase text-muted-foreground">UTR / Chq</div>{po.advance_utr || "-"}</div>
                <div><div className="text-[10px] uppercase text-muted-foreground">Mode</div>{po.advance_payment_mode || "-"}</div>
                {adv && (
                  <>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Already Adjusted</div><span className="font-mono">{fmtINR(adv.advance_adjusted_total)}</span></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Outstanding</div><span className="font-mono text-amber-700 font-semibold">{fmtINR(adv.advance_outstanding)}</span></div>
                  </>
                )}
              </div>
              {!po.advance_paid && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {canIssueAdvancePV && (
                    <Button variant="default" size="sm" className="bg-kabir text-black hover:bg-kabir-dark" onClick={createAdvancePV} data-testid="create-advance-pv-btn">
                      <Banknote size={14} className="mr-2" />Issue Advance PV (through system)
                    </Button>
                  )}
                  {canMarkAdvancePaidOutside && (
                    <Button variant="outline" size="sm" onClick={() => setAdvOpen(true)} data-testid="mark-advance-outside-btn">
                      Mark advance paid outside system
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">Approval Trail</div>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {audit.length === 0 && <div className="text-xs text-muted-foreground">No activity yet.</div>}
            {audit.map((a) => (
              <div key={a.id} className="text-xs border-l-2 border-kabir pl-3">
                <div className="font-semibold capitalize">{a.action.replace(/_/g, " ")}</div>
                <div className="text-muted-foreground">by {a.user_name}</div>
                <div className="text-muted-foreground">{fmtDateTime(a.created_at)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ---- GRNs card ---- */}
      <Card className="p-5 mt-4" data-testid="po-grns-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Deliveries (GRNs)</div>
            <div className="font-display text-lg font-semibold">{grns.length} GRN{grns.length !== 1 && "s"} booked</div>
          </div>
          {canBookGRN && (
            <Button variant="outline" onClick={openBookGRN} data-testid="book-grn-card-btn">
              <PackagePlus size={14} className="mr-2" />Book GRN
            </Button>
          )}
        </div>
        {grns.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-md">
            No deliveries yet against this PO.
          </div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">GRN No.</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Challan</th>
                  <th className="text-left px-3 py-2">Vehicle</th>
                  <th className="text-right px-3 py-2">Items</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {grns.map((g) => (
                  <tr key={g.id} className="border-t border-border hover:bg-secondary/30 cursor-pointer" onClick={() => nav(`/grns/${g.id}`)}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold">{g.grn_no}</td>
                    <td className="px-3 py-2">{fmtDate(g.created_at)}</td>
                    <td className="px-3 py-2">{g.challan_no || "-"}</td>
                    <td className="px-3 py-2">{g.vehicle_no || "-"}</td>
                    <td className="px-3 py-2 text-right">{g.items?.length || 0}</td>
                    <td className="px-3 py-2"><StatusBadge status={g.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <DocumentTrail trail={po.trail_ref} />

      {/* ---- Inline GRN dialog ---- */}
      <Dialog open={grnOpen} onOpenChange={setGrnOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="inline-grn-dialog">
          <DialogHeader><DialogTitle>Book GRN against {po.po_number}</DialogTitle></DialogHeader>
          {grnForm && (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <div><Label className="text-xs uppercase font-semibold">Challan No</Label><Input value={grnForm.challan_no} onChange={(e) => setGrnForm({ ...grnForm, challan_no: e.target.value })} data-testid="inline-grn-challan" /></div>
                <div><Label className="text-xs uppercase font-semibold">Vehicle No</Label><Input value={grnForm.vehicle_no} onChange={(e) => setGrnForm({ ...grnForm, vehicle_no: e.target.value })} data-testid="inline-grn-vehicle" /></div>
                <div><Label className="text-xs uppercase font-semibold">Driver</Label><Input value={grnForm.driver_name} onChange={(e) => setGrnForm({ ...grnForm, driver_name: e.target.value })} /></div>
                <div><Label className="text-xs uppercase font-semibold">Batch No</Label><Input value={grnForm.batch_no} onChange={(e) => setGrnForm({ ...grnForm, batch_no: e.target.value })} /></div>
                <div><Label className="text-xs uppercase font-semibold">Heat No</Label><Input value={grnForm.heat_no} onChange={(e) => setGrnForm({ ...grnForm, heat_no: e.target.value })} /></div>
                <div><Label className="text-xs uppercase font-semibold">Manufacturer</Label><Input value={grnForm.manufacturer} onChange={(e) => setGrnForm({ ...grnForm, manufacturer: e.target.value })} /></div>
              </div>
              <div className="border border-border rounded-md overflow-hidden mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-[10px] uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2">Material</th>
                      <th className="text-right px-2 py-2">Ordered</th>
                      <th className="text-right px-2 py-2">Prev. Recv</th>
                      <th className="text-right px-2 py-2">Received Now</th>
                      <th className="text-right px-2 py-2">Accepted</th>
                      <th className="text-right px-2 py-2">Rejected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grnForm.items.map((it, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-2 font-medium">{it.material_name}</td>
                        <td className="px-2 py-1 text-right">{it.ordered_qty}</td>
                        <td className="px-2 py-1 text-right text-muted-foreground">{it.previously_received}</td>
                        <td className="px-1 py-1"><Input type="number" className="h-8 text-right w-24" value={it.received_qty} onChange={(e) => setGrnForm({ ...grnForm, items: grnForm.items.map((x, idx) => idx === i ? { ...x, received_qty: parseFloat(e.target.value) || 0, accepted_qty: parseFloat(e.target.value) || 0 } : x) })} data-testid={`inline-grn-received-${i}`} /></td>
                        <td className="px-1 py-1"><Input type="number" className="h-8 text-right w-24" value={it.accepted_qty} onChange={(e) => setGrnForm({ ...grnForm, items: grnForm.items.map((x, idx) => idx === i ? { ...x, accepted_qty: parseFloat(e.target.value) || 0 } : x) })} /></td>
                        <td className="px-1 py-1"><Input type="number" className="h-8 text-right w-24" value={it.rejected_qty} onChange={(e) => setGrnForm({ ...grnForm, items: grnForm.items.map((x, idx) => idx === i ? { ...x, rejected_qty: parseFloat(e.target.value) || 0 } : x) })} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Textarea placeholder="Remarks…" rows={2} value={grnForm.remarks} onChange={(e) => setGrnForm({ ...grnForm, remarks: e.target.value })} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setGrnOpen(false)}>Cancel</Button>
                <PrimaryButton onClick={saveGRN} data-testid="inline-grn-save"><PackageCheck size={14} className="mr-2" />Record GRN</PrimaryButton>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Advance-paid-outside dialog ---- */}
      <Dialog open={advOpen} onOpenChange={setAdvOpen}>
        <DialogContent data-testid="mark-adv-outside-dialog">
          <DialogHeader><DialogTitle>Record advance already paid outside system</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase font-semibold">UTR / Cheque No *</Label><Input value={advPayForm.utr} onChange={(e) => setAdvPayForm({ ...advPayForm, utr: e.target.value })} data-testid="mark-adv-utr" /></div>
            <div><Label className="text-xs uppercase font-semibold">Mode</Label><Input value={advPayForm.mode} onChange={(e) => setAdvPayForm({ ...advPayForm, mode: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Bank</Label><Input value={advPayForm.bank} onChange={(e) => setAdvPayForm({ ...advPayForm, bank: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Date</Label><Input type="date" value={advPayForm.date} onChange={(e) => setAdvPayForm({ ...advPayForm, date: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvOpen(false)}>Cancel</Button>
            <PrimaryButton onClick={markAdvancePaidOutside} data-testid="mark-adv-save">Record Payment</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
